import {
  Box,
  Button,
  ContentTemplate,
  CustomAutocomplete,
  CustomSelect,
  HeaderInput,
  HeaderTemplate,
  MUIDataTable,
  MUIDataTableColumn,
  renderDropDownTags,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TableState,
  useDataTable,
  Checkbox
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import { Typography } from '@mui/material';
import { TFunction, useTranslation } from 'react-i18next';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import {
  DropDownItem,
  dropDownMappingPureArray,
  dropDownMappingToSelect,
  useAPIDropdown,
} from '../../../../hooks/dropdown';
import { useGetDataArchivalReportFilterArchivalLevel } from '../../../../api-refactored/data-archival-report/useGetDataArchivalReportFilterArchivalLevel';
import { useGetDataArchivalFilterYear } from '../../../../api-refactored/data-archival/useGetDataArchivalFilterYear';
import { usePostDataArchivalReportListing } from '../../../../api-refactored/data-archival-report/usePostDataArchivalReportListing';
import { searchDataArchivalReportSchema } from './schema/SearchDataArchivalReportSchema';
import DataArchivalReportDownloadLink from './DataArchivalReportDownloadLink';

const useStyles = makeStyles()((theme) =>
({
  header: {
    width: 480,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  container: {
    alignItems: 'center',
  },
  containerTable: {
    marginBottom: theme.spacing(7),
  },
  submitButtonContainer: {
    width: '100%',
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  byText: {
    color: theme.color.text.textTertiary,
  },
}));

const displayFilenameReportType = (
  fileName: string,
  reportType: string,
  dataIndex: number,
  classes: Record<string,string>
) => (
  <Box key={dataIndex.toString()}>
    {fileName}
    <br />
    <Typography className={classes.byText} variant="subtitle2">{`${reportType}`}</Typography>
  </Box>
);

interface TableColumnProps {
  state: TableState;
  classes: Record<string,string>;
  t: TFunction;
}

const tableColumns = ({ state, classes, t }: TableColumnProps): MUIDataTableColumn[] => [
  {
    name: 'fileName',
    label: t('admin->>DataArchivalReport.ColumnFileName'),
    options: {
      sort: false,
      filter: false,
      customBodyRenderLite: (dataIndex: number) =>
        displayFilenameReportType(
          state.data[dataIndex].fileName,
          state.data[dataIndex].reportType,
          dataIndex,
          classes
        ),
    },
  },
  {
    name: 'examLevelDescription',
    label: t('admin->>DataArchivalReport.ColumnExamLevel'),
    options: {
      setCellProps: () => ({ style: { minWidth: '150px' } }),
      sort: false,
    },
  },
  {
    name: 'fileUuid',
    label: t('admin->>DataArchivalReport.ColumnActions'),
    options: {
      sort: false,
      filter: false,
      setCellProps: () => ({ style: { width: '200px' } }),
      customBodyRender: (value: any) => <DataArchivalReportDownloadLink fileUuid={value} />,
    },
  },
];

interface DataArchivalReportSearchForm {
  examYear: String;
  examLevels: DropDownItem[];
}

const ListDataArchivalReport = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const {
    state,
    dispatch: tableDispatch,
    tOptions,
  } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'updatedAt',
    order: 'desc',
  });

  // Exam Year
  const filterYearApiResponse = useGetDataArchivalFilterYear({});
  const filterExamYear = useAPIDropdown(
    () => ({ data: filterYearApiResponse.data }),
    true,
    dropDownMappingPureArray
  );

  // Exam Level
  const filterExamLevelApiResponse = useGetDataArchivalReportFilterArchivalLevel({});
  const filterExamLevel = useAPIDropdown(
    () => ({ data: filterExamLevelApiResponse.data }),
    true,
    dropDownMappingToSelect
  );

  // create form
  const form = useForm<DataArchivalReportSearchForm>({
    mode: 'onSubmit',
    resolver: joiResolver(searchDataArchivalReportSchema()),
  });

  const { control, handleSubmit, register, formState: { errors }, setValue } = form;

  const onSubmit = (formData: DataArchivalReportSearchForm) => {
    tableDispatch({
      type: 'UPDATE_FILTERS',
      payload: {
        examYear: formData.examYear.toString(),
        examLevels: formData.examLevels.map((item: any) => item.value),
      },
    } as TableActionTypes);
  };

  const { mutate: listMutate, isLoading: listLoading } = usePostDataArchivalReportListing({
    onSuccess: (data) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: data.content,
          count: data.totalElements,
        },
      } as TableActionTypes);
    },
  });

  // effect
  useEffect(() => {
    if (state.filters.examYear) {
      listMutate({
        apiPathParam: {
          page: state.page,
          pageSize: state.size,
          sort: state.sortColumn,
          order: state.order,
        },
        examYear: state.filters.examYear,
        examLevels: state.filters.examLevels,
      });
    }
  }, [state.page, state.size, state.sortColumn, state.order, listMutate, state.filters]);

  return (
    <Box className={classes.container}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <HeaderTemplate>
          <HeaderInput
            label={t('admin->>DataArchivalReport.ExamYear')}
            isRequired
            input={
              <Box className={classes.header}>
                <CustomSelect
                  displayEmpty
                  isHeader
                  placeholder={t('admin->>DataArchivalReport.ExamYearPlaceholder')}
                  menuItems={filterExamYear.functionCode}
                  id="examYear"
                  name="examYear"
                  error={!!errors?.examYear}
                  helperText={errors?.examYear?.message?.toString()}
                  onChange={(event: any) => setValue('examYear', event.target.value)}
                  inputProps={{
                    inputRef: () => {
                      register('examYear');
                    },
                  }}
                />
              </Box>
            }
          />

          <HeaderInput
            label={t('admin->>DataArchivalReport.ExamLevel')}
            isRequired
            input={
              <Box className={classes.header}>
                <Controller
                  name="examLevels"
                  control={control}
                  render={({ field: { value } }) => (
                    <CustomAutocomplete<DropDownItem, true, false, undefined>
                      multiple
                      id="examLevels-old"
                      disableCloseOnSelect
                      placeholder={t('admin->>DataArchivalReport.ExamLevelPlaceholder')}
                      value={value}
                      options={filterExamLevel.functionCode as DropDownItem[]}
                      getOptionLabel={(option) => option.children}
                      isOptionEqualToValue={(option, v) => option.key === v.key}
                      renderOption={(_,option,{selected},__) => <>
                        <Checkbox style={{ padding: 0, margin: '0px 8px 0px 0px' }} checked={selected} />
                        {option.children}
                      </>}
                      renderTags={renderDropDownTags}
                      onChange={(_event: any, newValue) => setValue('examLevels', newValue)}
                      error={!!errors?.examLevels}
                      helperText={errors?.examLevels?.message?.toString()}
                    />
                  )}
                />
              </Box>
            }
          />

          <Box className={classes.submitButtonContainer}>
            <Button id="button-generate" type="submit">
              {t('admin->>PaymentReport.Generate')}
            </Button>
          </Box>
        </HeaderTemplate>

        <ContentTemplate type="full">
          <Box className={classes.containerTable}>
            <MUIDataTable
              state={state}
              isLoading={listLoading}
              dispatch={tableDispatch}
              columns={tableColumns({ state, classes, t })}
              data={state.data}
              options={{
                ...tOptions,
                filter: false,
                sort: false,
                pagination: true,
                sortOrder: { name: state.sortColumn, direction: state.order },
              }}
              emptyDataLabel={t('admin->>DataArchivalReport.EmptyDataArchivalReport')}
            />
          </Box>
        </ContentTemplate>
      </form>
    </Box>
  );
};

export default ListDataArchivalReport;
